import { cn } from '@ophelos/ui/lib/utils';
import * as React from 'react';
import { createContext, useContext } from 'react';

interface CardContextValue {
  isCollapsible?: boolean;
  isExpanded?: boolean;
  onToggle?: () => void;
  disabled?: boolean;
  showChevron?: boolean;
  variant?: CardVariant;
}

type CardVariant = 'default' | 'fullscreen';

const CardContext = createContext<CardContextValue>({});

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  isCollapsible?: boolean;
  isExpanded?: boolean;
  onToggle?: () => void;
  disabled?: boolean;
  showChevron?: boolean;
  /** "fullscreen" bleeds the layer to the screen edge below the xs breakpoint and squares off the inner content panel. */
  variant?: CardVariant;
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, isCollapsible, isExpanded, onToggle, disabled, showChevron = true, variant = 'default', ...props }, ref) => {
    return (
      <CardContext.Provider value={{ isCollapsible, isExpanded, onToggle, disabled, showChevron, variant }}>
        <div
          ref={ref}
          data-slot="card"
          data-state={isCollapsible && isExpanded ? 'open' : 'closed'}
          className={cn(
            'bg-soft shadow-none rounded-xl text-card-foreground flex flex-col gap-2 overflow-hidden',
            variant === 'fullscreen' ? 'max-xs:-mx-4 max-xs:rounded-none! pt-1.5 pb-2.5' : 'py-1.5',
            className
          )}
          {...props}
        />
      </CardContext.Provider>
    );
  }
);
Card.displayName = 'Card';

interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean;
}

const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(({ className, asChild, children, ...divProps }, ref) => {
  const { isCollapsible, onToggle, disabled } = useContext(CardContext);

  const headerClassName = cn(
    '@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start has-data-[slot=card-description]:gap-0 pl-5 pr-4 pt-2 has-data-[slot=card-action]:grid-cols-[1fr_auto] has-data-[slot=card-action]:gap-x-2! [.border-b]:pb-3',
    className
  );

  if (isCollapsible && !asChild) {
    // Separate CardAction from other children to avoid nesting buttons
    const childrenArray = React.Children.toArray(children);
    const isCardAction = (child: React.ReactNode): boolean => {
      if (!React.isValidElement(child)) return false;
      // Check by component reference or displayName
      return child.type === CardActionComponent || (child.type as { displayName?: string })?.displayName === 'CardAction';
    };
    const hasAction = childrenArray.some(isCardAction);
    const actionChild = childrenArray.find(isCardAction);
    const nonActionChildren = childrenArray.filter((child) => !isCardAction(child));

    // Use explicit grid columns when action is present to ensure proper layout
    const collapsibleHeaderClassName = cn(
      '@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start px-5 [.border-b]:pb-3',
      hasAction ? 'grid-cols-[1fr_auto] gap-3' : 'gap-1',
      className
    );

    return (
      <div ref={ref} data-slot="card-header" className={collapsibleHeaderClassName} {...divProps}>
        <button
          type="button"
          onClick={onToggle}
          disabled={disabled}
          className={cn(
            'col-start-1 col-end-2 row-span-2 row-start-1 flex items-baseline gap-3 text-left rounded-lg cursor-pointer px-3 -mx-3 py-1.5',
            hasAction && 'mr-0',
            disabled ? 'cursor-default opacity-50' : 'hover:bg-muted'
          )}
        >
          {nonActionChildren}
        </button>
        {actionChild && React.isValidElement(actionChild) ? React.cloneElement(actionChild as React.ReactElement) : actionChild}
      </div>
    );
  }

  return (
    <div ref={ref} data-slot="card-header" className={headerClassName} {...divProps}>
      {children}
    </div>
  );
});
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} data-slot="card-title" className={cn('leading-none font-medium text-base', className)} {...props} />
));
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} data-slot="card-description" className={cn('text-muted-foreground text-sm', className)} {...props} />
));
CardDescription.displayName = 'CardDescription';

let CardActionComponent: React.ForwardRefExoticComponent<React.HTMLAttributes<HTMLDivElement> & React.RefAttributes<HTMLDivElement>>;

const CardAction = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="card-action"
    className={cn('col-start-2 row-span-2 row-start-1 self-start justify-self-end flex items-center justify-end', className)}
    {...props}
  />
)) as typeof CardActionComponent;

CardAction.displayName = 'CardAction';
CardActionComponent = CardAction;

interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  forceRender?: boolean;
}

const CardContent = React.forwardRef<HTMLDivElement, CardContentProps>(({ className, forceRender, ...props }, ref) => {
  const { isCollapsible, isExpanded, variant } = useContext(CardContext);

  // If collapsible and not expanded (and not forced to render), don't render content
  if (isCollapsible && !isExpanded && !forceRender) {
    return null;
  }

  return (
    <div
      ref={ref}
      data-slot="card-content"
      className={cn('flex-1 p-4 bg-card border shadow-xs rounded-xl mx-1.5', variant === 'fullscreen' ? ' mx-2' : ' mx-1.5', className)}
      {...props}
    />
  );
});
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} data-slot="card-footer" className={cn('flex items-center px-6 [.border-t]:pt-6 mt-2.5', className)} {...props} />
));
CardFooter.displayName = 'CardFooter';

interface CardChevronProps extends React.ComponentProps<'svg'> {
  direction?: 'down' | 'right';
}

function CardChevron({ className, direction = 'down', ...props }: CardChevronProps) {
  const { isExpanded, isCollapsible } = useContext(CardContext);

  if (!isCollapsible) {
    return null;
  }

  const rotation = direction === 'right' ? (isExpanded ? 'rotate-90' : 'rotate-0') : isExpanded ? 'rotate-0' : '-rotate-90';

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      data-slot="card-chevron"
      className={cn('size-4 text-muted-foreground transition-transform duration-200', rotation, className)}
      {...props}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

export { Card, CardAction, CardChevron, CardContent, CardDescription, CardFooter, CardHeader, CardTitle };
